import { getOverrideBag } from './runtime-overrides';
import {
  KEY_DESCRIPTORS,
  runtimeKeys,
  type FlagRuntimeKey,
  type RuntimeKey,
} from './registry';
import {
  DEFAULT_RUNTIME_CONFIG,
  type RuntimeConfigSnapshot,
  type RuntimeConfig,
} from './types';

export type { RuntimeConfig, RuntimeConfigSnapshot };
export { runtimeKeys, type RuntimeKey, type FlagRuntimeKey };
export {
  RUNTIME_OVERRIDE_KEY,
  getRuntimeOverrides,
  getOverrideBagForPropagation,
} from './runtime-overrides';

export type RuntimeReader = {
  value(key: RuntimeKey): string;
  /** On/off URL flag with inherit when unset (`undefined`). */
  flag(key: FlagRuntimeKey): boolean | undefined;
  /** RUNTIME_CONFIG fields only — no overrides, no BUILD_CONFIG fallback. */
  snapshot(): RuntimeConfigSnapshot;
};

function stringifyOverrideValue(value: unknown): string {
  if (value === undefined || value === null) {
    return '';
  }
  return String(value);
}

function coerceFlag(raw: string): boolean | undefined {
  if (raw === '') {
    return undefined;
  }
  if (raw === 'false') {
    return false;
  }
  if (raw === 'true') {
    return true;
  }
  return true;
}

function resolveValue(key: RuntimeKey): string {
  const descriptor = KEY_DESCRIPTORS[key];
  const bag = getOverrideBag();

  if (Object.prototype.hasOwnProperty.call(bag, descriptor.overrideJsonKey)) {
    return stringifyOverrideValue(bag[descriptor.overrideJsonKey]);
  }

  if (descriptor.runtimeField && typeof window !== 'undefined') {
    const fromRuntime = window.RUNTIME_CONFIG?.[descriptor.runtimeField];
    if (fromRuntime !== undefined && fromRuntime !== '') {
      return fromRuntime;
    }
  }

  if (descriptor.buildField && typeof window !== 'undefined') {
    const fromBuild = window.BUILD_CONFIG?.[descriptor.buildField];
    if (fromBuild !== undefined && fromBuild !== '') {
      return fromBuild;
    }
  }

  return descriptor.default;
}

function resolveFlag(key: FlagRuntimeKey): boolean | undefined {
  return coerceFlag(resolveValue(key));
}

function buildSnapshot(): RuntimeConfigSnapshot {
  const runtime = typeof window !== 'undefined' ? window.RUNTIME_CONFIG : undefined;
  return {
    ASSET_SERVER: runtime?.ASSET_SERVER ?? DEFAULT_RUNTIME_CONFIG.ASSET_SERVER,
    PROVIDER_DIRECTORY_SDK:
      runtime?.PROVIDER_DIRECTORY_SDK ?? DEFAULT_RUNTIME_CONFIG.PROVIDER_DIRECTORY_SDK,
    GRID_LICENSE_KEY: runtime?.GRID_LICENSE_KEY ?? DEFAULT_RUNTIME_CONFIG.GRID_LICENSE_KEY,
    LICENSE_STORAGE_KEY:
      runtime?.LICENSE_STORAGE_KEY ?? DEFAULT_RUNTIME_CONFIG.LICENSE_STORAGE_KEY,
    USE_API_PROXY_ROUTES:
      runtime?.USE_API_PROXY_ROUTES ?? DEFAULT_RUNTIME_CONFIG.USE_API_PROXY_ROUTES,
    CASE_ACCELERATOR_UI_ENABLED:
      runtime?.CASE_ACCELERATOR_UI_ENABLED ??
      DEFAULT_RUNTIME_CONFIG.CASE_ACCELERATOR_UI_ENABLED,
    UI_LOG_LEVEL: runtime?.UI_LOG_LEVEL ?? DEFAULT_RUNTIME_CONFIG.UI_LOG_LEVEL,
    SESSION_HEARTBEAT_INTERVAL_SECONDS:
      runtime?.SESSION_HEARTBEAT_INTERVAL_SECONDS ??
      DEFAULT_RUNTIME_CONFIG.SESSION_HEARTBEAT_INTERVAL_SECONDS,
    FRONTEND_SESSION_FIRST:
      runtime?.FRONTEND_SESSION_FIRST ?? DEFAULT_RUNTIME_CONFIG.FRONTEND_SESSION_FIRST,
  };
}

const runtimeReader: RuntimeReader = {
  value: resolveValue,
  flag: resolveFlag,
  snapshot: buildSnapshot,
};

/** Sync read of deploy-time and override config for browser bundles. */
export function getRuntime(): RuntimeReader {
  return runtimeReader;
}

export { isCaseDetailTwoPhaseEnabled } from './case-accelerator-flag';
export {
  getSessionHeartbeatIntervalMs,
  getSessionHeartbeatIntervalSeconds,
} from './session-heartbeat-interval';
